import type { JSX } from 'react';

import Color from 'color';

import { Text } from '@change/ds-core-ui';

import { usePetitionDetailsPageData } from 'src/app/pages/petitionDetails/pageContext';

import { usePhoto } from '../hooks/photo';

import { MediaContainer } from './MediaSunrise';

const defaultGradient =
	'linear-gradient(to left, rgba(128,128,128,1) 0%, rgba(128,128,128,1) 79%, rgba(128,128,128,0.3) 95%, rgba(128,128,128,0) 100%)';

function generateGradient(rgb: { r: number; g: number; b: number }): string {
	try {
		const baseColor = Color(rgb);
		const color1 = baseColor.rgb().string();
		const color2 = baseColor.alpha(0.3).rgb().string();
		const color3 = baseColor.alpha(0).rgb().string();

		return `linear-gradient(to left, ${color1} 0%, ${color1} 79%, ${color2} 95%, ${color3} 100%)`;
	} catch {
		return defaultGradient;
	}
}

export function HeadingContainer(): JSX.Element {
	const {
		data: { petition },
	} = usePetitionDetailsPageData();
	const photo = usePhoto();
	const title: string = petition.displayTitle;
	const averageColor = photo?.palette?.averageColor as { r: number; g: number; b: number } | undefined;
	const gradientValue = averageColor ? generateGradient(averageColor) : defaultGradient;
	return (
		<div
			data-experiment="petition-header-experiment"
			className="absolute max-h-[762px] w-full space-y-2 overflow-hidden rounded-xl bg-black md:aspect-4/3 lg:aspect-18/10"
		>
			<div className="relative h-full w-full">
				<div className="mb-[100px] w-full">
					<div className="h-auto w-[1005px] [@media(max-width:1005px)]:w-full">
						{!!photo && <MediaContainer key={petition.id} photo={photo} />}
					</div>
					<div
						className="absolute top-0 right-0 bottom-0 z-10 h-full w-[36%]"
						style={{ backgroundImage: gradientValue }}
					></div>
					<div className="absolute inset-[46%] right-0 bottom-0 left-0 z-10 h-[92%] w-full bg-[linear-gradient(to_top,rgba(0,0,0,1)_48%,rgba(0,0,0,1)_70%,rgba(0,0,0,0.8)_75%,rgba(0,0,0,0)_87%)]" />
					<div className="absolute right-0 bottom-[35px] left-0 z-11 px-4 py-4 sm:px-8 sm:py-[40px]">
						<div className="space-y-2">
							<div className="mb-[10px] h-[12px] w-[71.2px] bg-[#E5231E]"></div>
							<Text
								data-qa="petition-title"
								variant="heading"
								as="h2"
								size="x-large"
								className="max-w-[72%] text-xl leading-tight font-bold text-balance text-white sm:text-2xl md:max-w-[72%] lg:text-3xl"
							>
								{title}
							</Text>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
